// ============================================
// 敵スポーン管理
// ============================================

import { CONFIG } from '../../config/game-config';
import { ENEMY_CONFIG } from '../../config/enemy-config';
import {
    getWave,
    getLastSpawnLane,
    getSameLaneCount,
    setLastSpawnLane,
    setSameLaneCount,
    getEnemies
} from '../../store/index';
import { getP5 } from '../../core/p5-instance';
import { createEnemy } from './enemy-factory';
import type { EnemyType } from '../../types/game-types';

/**
 * spawnEnemy() - 通常敵の生成
 */
export function spawnEnemy(): void {
    const wave = getWave();
    let lastSpawnLane = getLastSpawnLane();
    let sameLaneCount = getSameLaneCount();
    const enemies = getEnemies();

    // レーン選択（同じレーンに連続しにくくする）
    const lane = selectSpawnLane(lastSpawnLane, sameLaneCount);

    // レーン連続カウントの更新
    if (lane === lastSpawnLane) {
        sameLaneCount++;
    } else {
        sameLaneCount = 1;
    }
    lastSpawnLane = lane;
    setLastSpawnLane(lastSpawnLane);
    setSameLaneCount(sameLaneCount);

    // 現在チャージ中または発射中のLASER敵がいるレーンを取得
    const laserLanes = getLaserActiveLanes(enemies);

    // 敵タイプを選択
    const chosenType = selectEnemyType(wave, laserLanes);

    // 生成
    const enemy = createEnemy(chosenType, lane, laserLanes);
    if (enemy) {
        enemies.push(enemy);
    }
}

/**
 * レーン選択ロジック（同じレーンに連続しにくくする）
 */
function selectSpawnLane(lastSpawnLane: number, sameLaneCount: number): number {
    const p = getP5();

    if (lastSpawnLane >= 0 && sameLaneCount >= 2) {
        // 3回以上同じレーンに連続する場合、必ず別のレーンを選択
        const otherLanes: number[] = [];
        for (let i = 0; i < CONFIG.laneCount; i++) {
            if (i !== lastSpawnLane) {
                otherLanes.push(i);
            }
        }
        return otherLanes[p.floor(p.random(otherLanes.length))];
    } else if (lastSpawnLane >= 0 && p.random() < 0.3) {
        // 30%の確率で前回と違うレーンを優先
        const otherLanes: number[] = [];
        for (let i = 0; i < CONFIG.laneCount; i++) {
            if (i !== lastSpawnLane) {
                otherLanes.push(i);
            }
        }
        return otherLanes[p.floor(p.random(otherLanes.length))];
    } else {
        // 通常のランダム選択
        return p.floor(p.random(CONFIG.laneCount));
    }
}

/**
 * LASER敵がアクティブなレーンを取得
 */
function getLaserActiveLanes(enemies: ReturnType<typeof getEnemies>): number[] {
    const laserLanes: number[] = [];
    for (const e of enemies) {
        if (e.type === 'laser' && (e.laserState === 'charging' || e.laserState === 'firing')) {
            laserLanes.push(e.lane);
        }
    }
    return laserLanes;
}

/**
 * 敵タイプの選択（重み付き抽選）
 */
function selectEnemyType(wave: number, laserLanes: number[]): EnemyType {
    const p = getP5();

    // ウェーブが進むと強い敵が出やすくなる（通常敵の確率が減少）
    const waveBonus = p.min(wave * 2, CONFIG.enemy.progression.typeProgressionMax * 100);

    // 汎用アンロック: 各敵タイプの minWave でフィルタし、重み付き抽選
    const types: EnemyType[] = ['normal', 'tank', 'fast', 'armored', 'speedy', 'laser', 'mirror'];
    const pool: Array<{ type: EnemyType; weight: number }> = [];

    for (const t of types) {
        if (t === 'boss') {
            continue;
        }
        const cfg = ENEMY_CONFIG[t];
        const minW = cfg.minWave || 1;
        if (wave >= minW) {
            let w = cfg.spawnWeight;
            if (t === 'normal') {
                w = p.max(0, cfg.spawnWeight - waveBonus);
            }
            // 2レーンの場合、LASER敵が1体でもチャージ/発射中なら新しいLASERは出現しない
            // （片方のレーンが常に安全であることを保証）
            if (t === 'laser' && laserLanes.length >= 1) {
                w = 0;
            }
            if (w > 0) {
                pool.push({ type: t, weight: w });
            }
        }
    }

    // 万が一プールが空のときは NORMAL をフォールバック
    if (pool.length === 0) {
        return 'normal';
    }

    // 重みの合計からルーレット選択
    let totalWeight = 0;
    for (const poolItem of pool) {
        totalWeight += poolItem.weight;
    }
    const roll: number = p.random(totalWeight);
    let cumulative = 0;
    for (const poolItem of pool) {
        cumulative += poolItem.weight;
        if (roll < cumulative) {
            return poolItem.type;
        }
    }

    return pool[0].type;
}
